import { useState } from "react";
import { useAuth } from "../hooks/useAuth";

interface Props {
  open: boolean;
  onClose: () => void;
}

type View = "menu" | "login" | "register";

export function AccountFlyout({ open, onClose }: Props) {
  const { user, loading, signInWithGoogle, signInWithEmail, registerWithEmail, signOut } = useAuth();
  const [view, setView] = useState<View>("menu");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function handleGoogle() {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle();
      onClose();
    } catch (err) {
      setError(prettyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      if (view === "register") {
        await registerWithEmail(email.trim(), password);
      } else {
        await signInWithEmail(email.trim(), password);
      }
      setEmail("");
      setPassword("");
      onClose();
    } catch (err) {
      setError(prettyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleSignOut() {
    setError(null);
    setBusy(true);
    try {
      await signOut();
    } catch (err) {
      setError(prettyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="flyout-backdrop" onClick={onClose} />
      <aside className="flyout" role="dialog" aria-label="Account">
        <header className="flyout-head">
          <button className="flyout-close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="flyout-body">
          {loading && (
            <div className="flyout-soon">
              <span className="flyout-soon-emoji">⏳</span>
              <p>Loading…</p>
            </div>
          )}

          {/* Signed in: show profile + sign out */}
          {!loading && user && (
            <div className="flyout-form">
              <h3>{user.displayName || "Signed in"}</h3>
              {user.photoURL && (
                <img
                  src={user.photoURL}
                  alt=""
                  style={{ width: 64, height: 64, borderRadius: "50%", margin: "8px 0" }}
                />
              )}
              <p className="flyout-hint" style={{ marginTop: 4 }}>
                {user.email || "(no email)"}
              </p>
              <p className="flyout-hint" style={{ marginTop: 12 }}>
                Your designs still live in this browser. Cross-device sync is
                coming soon — for now signing in just identifies you.
              </p>
              {error && <p style={{ color: "#dc2626", fontSize: 12, marginTop: 8 }}>{error}</p>}
              <button
                className="btn"
                style={{ width: "100%", marginTop: 12 }}
                onClick={handleSignOut}
                disabled={busy}
              >
                {busy ? "…" : "Sign out"}
              </button>
            </div>
          )}

          {/* Signed out — menu */}
          {!loading && !user && view === "menu" && (
            <div className="flyout-form">
              <h3>Sign in to www.logoman.pro</h3>

              <button
                className="btn primary big"
                style={{ width: "100%", marginTop: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                onClick={handleGoogle}
                disabled={busy}
              >
                <GoogleGlyph />
                <span>{busy ? "…" : "Continue with Google"}</span>
              </button>

              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0", color: "#94a3b8", fontSize: 12 }}>
                <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
                <span>or</span>
                <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
              </div>

              <ul className="flyout-menu">
                <li><button onClick={() => { setView("login"); setError(null); }}>Log in with email</button></li>
                <li><button onClick={() => { setView("register"); setError(null); }}>Create an email account</button></li>
              </ul>

              {error && <p style={{ color: "#dc2626", fontSize: 12, marginTop: 8 }}>{error}</p>}

              <p className="flyout-hint" style={{ marginTop: 16 }}>
                Designs always auto-save in your browser — no account needed to design or download. Accounts are for cross-device sync (coming soon).
              </p>
            </div>
          )}

          {/* Signed out — email form */}
          {!loading && !user && (view === "login" || view === "register") && (
            <form className="flyout-form" onSubmit={handleEmailSubmit}>
              <button type="button" className="flyout-back" onClick={() => { setView("menu"); setError(null); }}>&larr; Back</button>
              <h3>{view === "login" ? "Log in" : "Create your account"}</h3>
              <label className="field">
                <span>Email</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" disabled={busy} />
              </label>
              <label className="field">
                <span>Password</span>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete={view === "login" ? "current-password" : "new-password"} disabled={busy} />
              </label>
              <button
                type="submit"
                className="btn primary big"
                style={{ width: "100%", marginTop: 4 }}
                disabled={busy}
              >
                {busy ? "…" : view === "login" ? "Log in" : "Create account"}
              </button>
              {error && <p style={{ color: "#dc2626", fontSize: 12, marginTop: 8 }}>{error}</p>}
            </form>
          )}
        </div>

        <footer className="flyout-foot">
          <a href="#help" onClick={(e) => e.preventDefault()}>Help / Contact</a>
          <a href="#terms" onClick={(e) => e.preventDefault()}>Terms of Service</a>
          <a href="#privacy" onClick={(e) => e.preventDefault()}>Privacy</a>
        </footer>
      </aside>
    </>
  );
}

/** Map raw Firebase error codes to user-friendly copy. */
function prettyAuthError(err: unknown): string {
  const code = (err as { code?: string })?.code ?? "";
  switch (code) {
    case "auth/invalid-email":            return "That email address doesn't look right.";
    case "auth/user-not-found":           return "No account found with that email.";
    case "auth/wrong-password":           return "Wrong password.";
    case "auth/invalid-credential":       return "Email or password is incorrect.";
    case "auth/email-already-in-use":     return "An account with that email already exists.";
    case "auth/weak-password":            return "Password should be at least 6 characters.";
    case "auth/popup-closed-by-user":     return "Sign-in cancelled.";
    case "auth/popup-blocked":            return "Your browser blocked the sign-in popup. Allow popups for this site and try again.";
    case "auth/network-request-failed":   return "Network error — check your connection and try again.";
    case "auth/too-many-requests":        return "Too many attempts. Try again in a minute.";
    default:
      return (err as Error)?.message || "Something went wrong. Try again.";
  }
}

/** Tiny Google logo SVG so we don't pull in a 1MB icon library. */
function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8c1.8-4.4 6.1-7.5 11.1-7.5 3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.5-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.3 2.4-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.6l6.2 5.2C41.9 35.3 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/>
    </svg>
  );
}
